import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FiLock, FiCheckSquare, FiSquare, FiPlus } from 'react-icons/fi';
import { getTypeMeta, getStatusMeta } from '../lib/sharedListTypes';
import './PublicSharedList.css';

const PublicSharedList = () => {
  const { token } = useParams();
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  const [stage, setStage] = useState('loading');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [authing, setAuthing] = useState(false);
  const [data, setData] = useState(null);
  const [externalUserName, setExternalUserName] = useState(
    () => localStorage.getItem('aa.share.externalUserName') || ''
  );
  const [draftText, setDraftText] = useState('');
  const [busy, setBusy] = useState(false);
  const [opError, setOpError] = useState('');

  const persistName = (v) => {
    setExternalUserName(v);
    try { localStorage.setItem('aa.share.externalUserName', v); } catch (_e) {}
  };

  const loadList = async (pw = '') => {
    try {
      const url = `${apiUrl}/api/public/shared-list/${token}${pw ? `?password=${encodeURIComponent(pw)}` : ''}`;
      const r = await fetch(url);
      if (r.status === 401) { setStage('password'); return false; }
      if (r.status === 404) { setStage('error'); setError('This share link is no longer active.'); return false; }
      if (!r.ok) throw new Error('Failed to load');
      const d = await r.json();
      setData(d); setStage('view'); return true;
    } catch (e) { setStage('error'); setError(e.message); return false; }
  };

  useEffect(() => { loadList(''); /* eslint-disable-next-line */ }, [token]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setAuthing(true); setError('');
    try {
      const r = await fetch(`${apiUrl}/api/public/shared-list/${token}/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!r.ok) { setError('Wrong password. Please try again.'); return; }
      await loadList(password);
    } catch (e2) { setError(e2.message); }
    finally { setAuthing(false); }
  };

  const sl = data?.sharedList;
  const meta = useMemo(() => (sl ? getTypeMeta(sl.type) : null), [sl]);
  const cols = meta?.columns || {};
  const perms = sl?.permissions || {};
  const Icon = meta?.icon || null;

  const callPublic = async (path, method, body = {}) => {
    setBusy(true); setOpError('');
    try {
      const r = await fetch(`${apiUrl}/api/public/shared-list/${token}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, password: password || undefined, externalUserName }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      await loadList(password);
      return await r.json().catch(() => ({}));
    } catch (e) { setOpError(e.message); }
    finally { setBusy(false); }
  };

  const addItem = async () => {
    if (!draftText.trim()) return;
    if (!externalUserName.trim()) { setOpError('Please enter your name first.'); return; }
    await callPublic('/items', 'POST', { text: draftText.trim() });
    setDraftText('');
  };

  const toggleComplete = async (item) => {
    if (!externalUserName.trim()) { setOpError('Please enter your name first.'); return; }
    const done = item.status === 'done';
    await callPublic(`/items/${item.id}`, 'PATCH', { completed: !done });
  };

  const editItem = async (item, patch) => {
    if (!externalUserName.trim()) { setOpError('Please enter your name first.'); return; }
    await callPublic(`/items/${item.id}`, 'PATCH', patch);
  };

  if (stage === 'loading') return <div className="public-page"><div className="loading-spinner" /></div>;
  if (stage === 'error') return (
    <div className="public-page">
      <div className="public-card error-card">
        <div className="public-card-title">Link unavailable</div>
        <div className="public-card-text">{error || 'This share link is no longer active.'}</div>
      </div>
    </div>
  );
  if (stage === 'password') return (
    <div className="public-page">
      <form className="public-card password-card" onSubmit={submitPassword}>
        <div className="password-icon"><FiLock size={28} /></div>
        <div className="public-card-title">Password required</div>
        <div className="public-card-text">This shared list is password-protected.</div>
        <input type="password" className="input" placeholder="Enter password" value={password}
               onChange={(e) => setPassword(e.target.value)} autoFocus
               data-testid="public-password-input" />
        {error && <div className="form-error">{error}</div>}
        <button type="submit" className="btn btn-primary" disabled={authing || !password}
                data-testid="public-password-submit">{authing ? 'Checking...' : 'Unlock'}</button>
      </form>
    </div>
  );

  const canAct = perms.canAddItems || perms.canCompleteItems || perms.canEditItems || perms.canComment;

  return (
    <div className="public-page">
      <div className="public-container" data-testid="public-shared-list">
        <div className="public-header">
          <div className="public-header-icon" style={{ background: `${meta.color}1A`, color: meta.color }}>
            {Icon && <Icon size={24} />}
          </div>
          <div className="public-header-body">
            <div className="public-header-type" style={{ color: meta.color }}>{meta.label}</div>
            <h1 className="public-header-title" data-testid="public-title">{sl.title}</h1>
            {sl.description && <div className="public-header-desc">{sl.description}</div>}
          </div>
        </div>

        {canAct && (
          <div data-testid="public-name-row" style={{
            display: 'flex', gap: 10, alignItems: 'center', padding: 12,
            background: '#F6F7FB', borderRadius: 8, marginBottom: 16,
          }}>
            <label style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Your name</label>
            <input className="input" style={{ flex: 1, maxWidth: 280 }}
                   placeholder="e.g. Ahmed (client)" value={externalUserName}
                   onChange={(e) => persistName(e.target.value)}
                   data-testid="public-external-name" />
            <span style={{ fontSize: 11, color: '#94a3b8' }}>Shown beside your actions</span>
          </div>
        )}

        {perms.canSeeTotals && data.totals && (
          <div className="public-totals">
            <div className="public-totals-card">
              <div className="public-totals-label">Subtotal</div>
              <div className="public-totals-value">{Number(data.totals.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div className="public-totals-card">
              <div className="public-totals-label">Paid</div>
              <div className="public-totals-value" style={{ color: '#1DBF73' }}>{Number(data.totals.paid).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div className="public-totals-card">
              <div className="public-totals-label">Remaining</div>
              <div className="public-totals-value" style={{ color: data.totals.remaining > 0 ? '#E24B5B' : '#1DBF73' }}>{Number(data.totals.remaining).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>
        )}

        <div className="public-table">
          {data.items.length === 0 ? (
            <div className="empty-state">No items yet.</div>
          ) : data.items.map((it) => {
            const done = it.status === 'done';
            return (
              <div key={it.id} className={`public-row ${done ? 'done' : ''}`} data-testid="public-row">
                {perms.canCompleteItems ? (
                  <button className={`sld-check ${done ? 'checked' : ''}`} onClick={() => toggleComplete(it)}
                          disabled={busy} style={{ flexShrink: 0, marginRight: 4 }}
                          data-testid="public-check"
                          aria-label={done ? 'Mark as open' : 'Mark as done'}>
                    {done && <span style={{ color: '#fff', fontSize: 13 }}>✓</span>}
                  </button>
                ) : cols.check ? (
                  <div className="public-row-check">
                    {done ? <FiCheckSquare size={18} color="#1DBF73" /> : <FiSquare size={18} color="#cbd5e1" />}
                  </div>
                ) : null}
                <div className="public-row-main">
                  {perms.canEditItems ? (
                    <input defaultValue={it.text || ''}
                           onBlur={(e) => {
                             const v = e.target.value.trim();
                             if (v && v !== (it.text || '')) editItem(it, { text: v });
                           }}
                           style={{ border: 'none', background: 'transparent', fontSize: 14, fontWeight: 500, color: '#0F172A', width: '100%' }}
                           data-testid="public-row-text-input" />
                  ) : (
                    <div className="public-row-text">{it.text}</div>
                  )}
                  {cols.description && it.description && <div className="public-row-desc">{it.description}</div>}
                  {it.completedByName && done && (
                    <div className="public-row-desc" style={{ fontStyle: 'italic' }}>Done by {it.completedByName}</div>
                  )}
                </div>
                {cols.location && it.location && <div className="public-row-cell">{it.location}</div>}
                {cols.company && it.responsibleCompany && <div className="public-row-cell">{it.responsibleCompany}</div>}
                {perms.canSeeTotals && cols.qty && (<div className="public-row-cell num">{it.quantity ?? '—'}</div>)}
                {perms.canSeeTotals && cols.money && (
                  <>
                    <div className="public-row-cell num">{it.unitPrice != null ? Number(it.unitPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</div>
                    <div className="public-row-cell num strong">{it.amount != null ? Number(it.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</div>
                  </>
                )}
                {cols.status && (
                  <div className="public-row-cell">
                    <span className="public-status" style={{ background: `${getStatusMeta(it.status || 'open').color}1A`, color: getStatusMeta(it.status || 'open').color }}>
                      {getStatusMeta(it.status || 'open').label}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {perms.canAddItems && (
          <div data-testid="public-add-row" style={{
            display: 'flex', gap: 8, marginTop: 16, padding: 12, background: '#F6F7FB', borderRadius: 8,
          }}>
            <input className="input" style={{ flex: 1 }} placeholder="Add a new item..."
                   value={draftText} onChange={(e) => setDraftText(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && addItem()}
                   data-testid="public-add-input" />
            <button className="btn btn-primary" onClick={addItem}
                    disabled={busy || !draftText.trim() || !externalUserName.trim()}
                    data-testid="public-add-button">
              <FiPlus size={14} /> Add
            </button>
          </div>
        )}

        {opError && <div className="form-error" style={{ marginTop: 12 }} data-testid="public-op-error">{opError}</div>}

        <div className="public-footer">Powered by AA Architecture PM</div>
      </div>
    </div>
  );
};

export default PublicSharedList;
